import { FC } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Label,
  LoadingButton,
  NativeSelect,
  RadioGroup,
  RadioGroupItem,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@altimateai/lego";
import { IconBuilding, IconLink, IconUsers, IconWorld } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import {
  ShareDialogProps,
  EXPIRATION_OPTIONS,
  ACCESS_TYPE_OPTIONS,
  SHARE_TAB_PARAM,
  SHARE_TAB_ITEMS,
  resolveShareTab,
  useShareDialog,
} from "./useShareDialog";
import ShareLinksList from "./ShareLinksList";
import ShareSlackTab from "./ShareSlackTab";
import ShareEmailTab from "./ShareEmailTab";
import ShareTeamCombobox from "./ShareTeamCombobox";
import ShareUserCombobox from "./ShareUserCombobox";

const ACCESS_ICONS = { world: IconWorld, users: IconUsers, building: IconBuilding } as const;

const ShareDialog: FC<ShareDialogProps> = ({ sessionId, sessionName, open, onOpenChange }) => {
  // Tab state in URL params so it survives dialog close/reopen (react-patterns.md).
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveShareTab(searchParams.get(SHARE_TAB_PARAM));
  const setActiveTab = (next: string) =>
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev);
        p.set(SHARE_TAB_PARAM, resolveShareTab(next));
        return p;
      },
      { replace: true }
    );
  const {
    accessType,
    handleAccessTypeChange,
    expiresInDays,
    setExpiresInDays,
    selectedUserIds,
    setSelectedUserIds,
    selectedTeamIds,
    setSelectedTeamIds,
    isLoadingLinks,
    isCreating,
    isDeleting,
    createLink,
    removeLink,
    formatExpiration,
    canCreate,
    shareLinks,
  } = useShareDialog(sessionId, sessionName, open);

  const TitleIcon = SHARE_TAB_ITEMS.find(t => t.value === activeTab)?.icon ?? IconLink;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:al-max-w-[620px] al-max-h-[85vh] al-flex al-flex-col al-overflow-hidden")}
      >
        <DialogHeader>
          <DialogTitle>
            <Stack direction="row" align="center" gap="sm">
              <TitleIcon className="al-h-5 al-w-5" />
              Share Conversation
            </Stack>
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className={cn("al-min-h-0 al-min-w-0 al-flex al-flex-col al-flex-1")}
        >
          <TabsList>
            {SHARE_TAB_ITEMS.map(tab => (
              <TabsTrigger key={tab.value} value={tab.value}>
                <tab.icon className="al-h-4 al-w-4 al-mr-1.5" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent
            value="link"
            className="al-overflow-y-auto al-overflow-x-hidden al-min-h-0 al-min-w-0"
          >
            <div className="al-space-y-4 al-py-4">
              {/* Access Type Selection */}
              <div className="al-space-y-3">
                <Label>Who can access</Label>
                <RadioGroup
                  value={accessType}
                  onValueChange={handleAccessTypeChange}
                  className="al-space-y-2"
                >
                  {ACCESS_TYPE_OPTIONS.map(opt => {
                    const Icon = ACCESS_ICONS[opt.icon];
                    return (
                      <label
                        key={opt.value}
                        htmlFor={opt.value}
                        className={cn(
                          "al-flex al-items-center al-space-x-3 al-p-3 al-rounded-md al-border al-cursor-pointer hover:al-bg-muted/50"
                        )}
                      >
                        <RadioGroupItem value={opt.value} id={opt.value} />
                        <Icon className="al-h-5 al-w-5 al-text-muted-foreground" />
                        <div className="al-flex-1">
                          <span className="al-font-medium">{opt.label}</span>
                          <p className="al-text-sm al-text-muted-foreground">{opt.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              </div>

              {/* User picker (only for specific_people) */}
              {accessType === "specific_people" && (
                <ShareUserCombobox
                  selectedUserIds={selectedUserIds}
                  onChange={setSelectedUserIds}
                />
              )}

              {/* Team picker (only for team access type) */}
              {accessType === "team" && (
                <ShareTeamCombobox
                  selectedTeamIds={selectedTeamIds}
                  onChange={setSelectedTeamIds}
                />
              )}

              {/* Expiration and Create */}
              <div className="al-space-y-3">
                <Label>Link expiration</Label>
                <Stack direction="row" align="center" gap="sm">
                  <NativeSelect
                    options={EXPIRATION_OPTIONS}
                    value={expiresInDays}
                    onChange={setExpiresInDays}
                    placeholder="Expiration"
                    className="al-w-[140px]"
                  />
                  <LoadingButton
                    loading={isCreating}
                    onClick={() => createLink()}
                    disabled={!canCreate}
                  >
                    <IconLink className="al-h-4 al-w-4 al-mr-2" />
                    Create Link
                  </LoadingButton>
                </Stack>
              </div>

              <ShareLinksList
                shareLinks={shareLinks}
                isLoadingLinks={isLoadingLinks}
                isDeleting={isDeleting}
                onRemoveLink={removeLink}
                formatExpiration={formatExpiration}
              />
            </div>
          </TabsContent>

          <TabsContent
            value="slack"
            className="al-py-4 al-overflow-y-auto al-overflow-x-hidden al-min-h-0 al-min-w-0"
          >
            <ShareSlackTab
              sessionId={sessionId}
              open={open}
              onSuccess={() => onOpenChange(false)}
            />
          </TabsContent>

          <TabsContent
            value="email"
            className="al-overflow-y-auto al-overflow-x-hidden al-min-h-0 al-min-w-0"
          >
            <ShareEmailTab sessionId={sessionId} onSuccess={() => onOpenChange(false)} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
